import { Injectable } from '@angular/core';
import { numberValueConversion } from '@mds/common';
import { PIE_CHART_COLORS } from '@pages/data-statistics/models/data-statistics';

export enum ChartType {
  PROGRESS_BAR = 'progressBar',
  COLUMN = 'column',
  PIE = 'pie'
}

export interface MapOptions {
  attribution?: string;
  tileSize: number;
  maxZoom: number;
  zoomOffset: number;
  noWrap: boolean;
  bounds: Array<number[]>;
  id: string;
}

declare const leafletImage;

@Injectable({ providedIn: 'root' })
export class ChartLibraryService {
  exportLeafletImage(map, filename) {
    return new Promise((resolve, reject) => {
      if (!leafletImage) reject();
      leafletImage(map, (err, canvas) => {
        const img = document.createElement('img');
        const dimensions = map.getSize();
        img.width = dimensions.x > 1024 ? 1024 : dimensions.x;
        img.height = dimensions.y;
        img.src = canvas.toDataURL();
        const a = document.createElement('a'); // Create anchor for download
        a.href = img.src; // Image Base64 Goes here
        a.download = filename;
        setTimeout(() => {
          a.click();
          a.remove();
          resolve('');
        }, 200);
      });
    });
  }

  // This function is used to support headers with api call
  // Now we are call our server api for map images and our server demand Authorization request headers
  addHeadersSupportToLeaflet(L: any) {
    L.TileLayerWithHeaders = L.TileLayer.extend({
      // we can't use arrow function as arrow function doesn't have its own this and use parent this
      // and we need this of TileLayer class not our service

      initialize: function (url, options, headers, abort) {
        L.TileLayer.prototype.initialize.call(this, url, options);
        this.headers = headers;
        this.abort = abort;
      },
      createTile(coords, done) {
        const url = this.getTileUrl(coords);
        const img = document.createElement('img');
        img.setAttribute('role', 'presentation');
        fetchImage(
          url,
          (resp) => {
            const reader = new FileReader();
            reader.onload = () => {
              img.src = <any>reader.result;
            };
            reader.readAsDataURL(resp);
            done(null, img);
          },
          this.headers,
          this.abort
        );
        return img;
      }
    });
    L.tileLayer = function (url, options, headers, abort) {
      return new L.TileLayerWithHeaders(url, options, headers, abort);
    };
  }

  getMapTiles(L: any, options: MapOptions) {
    this.addHeadersSupportToLeaflet(L);
    const tiles = L.tileLayer(this.mapURL, options, [
      { header: 'Authorization', value: `Bearer ${localStorage.getItem('auth_token')}` }
    ]);
    return tiles;
  }

  get mapURL() {
    return '/api/v1/pinmap/{id}?z={z}&x={x}&y={y}';
  }

  getBaseChartOptions(unit = null): Partial<Highcharts.Options> {
    return {
      title: {
        text: '',
        margin: 5
      },
      credits: {
        enabled: false
      },
      tooltip: {
        pointFormat: `<b>{point.y}</b>${unit}`
      },
      legend: {
        symbolRadius: 0
      },
      colors: PIE_CHART_COLORS,
      accessibility: { enabled: false },
      series: []
    };
  }

  getColumnChartOptions(unit?: string): Partial<Highcharts.Options> {
    return {
      ...this.getBaseChartOptions(unit),
      chart: {
        type: 'column'
      },
      plotOptions: {
        column: {
          pointPadding: 0
        }
      }
    };
  }

  getPieChartOptions(value: string, subtitle: string, unit?: string): Partial<Highcharts.Options> {
    return {
      ...this.getBaseChartOptions(unit),
      ...this.getCenteredChartSubtitle(value, subtitle),
      chart: {
        type: 'pie',
        animation: true,
        width: null,
        reflow: true,
        spacingLeft: 0,
        spacingRight: 0
      },
      plotOptions: {
        pie: {
          innerSize: '75%',
          dataLabels: {
            enabled: false
          },
          borderWidth: 8,
          borderColor: '#ffffff',
          showInLegend: true,
          ignoreHiddenPoint: false
        }
      }
    } as Partial<Highcharts.Options>;
  }

  getPercentageBarChartOptions(): Partial<Highcharts.Options> {
    return {
      ...this.getBaseChartOptions('%'),
      chart: {
        type: 'bar',
        height: 150
      },
      plotOptions: {
        series: {
          stacking: 'percent'
        },
        bar: {
          pointWidth: 32,
          pointPadding: 0,
          groupPadding: 0
        }
      },
      yAxis: { min: 0, max: 100, maxPadding: 0.5, visible: false },
      xAxis: { visible: false },
      legend: {
        reversed: true,
        symbolRadius: 0
      },
      tooltip: {
        headerFormat: '',
        pointFormat: '{series.name}: {y} <b>{point.y}</b> ({point.percentage:.0f}%)'
      }
    };
  }

  private getCenteredChartSubtitle(value: number | string, subtitle: string) {
    return {
      subtitle: {
        text: `<div style="text-align:center;">
                <b id="subtitle-value" style="font-size:16px; color:#003C4F;">${
                  value ? numberValueConversion(value) : ''
                }</b><br/>
                <span id="subtitle-unit" style="font-size:12px; color:#547080;">${subtitle}</span>
             </div>`,
        useHTML: true,
        align: 'center',
        verticalAlign: 'middle',
        y: 0,
        style: {
          fontSize: '16px',
          color: '#547080'
        }
      }
    };
  }
}

async function fetchImage(url, callback, headers, abort) {
  const _headers: any = {};
  if (headers) {
    headers.forEach((h) => {
      _headers[h.header] = h.value;
    });
  }
  const controller = new AbortController();
  const { signal } = controller;
  if (abort) {
    abort.subscribe(() => {
      controller.abort();
    });
  }
  const f = await fetch(url, {
    method: 'GET',
    headers: _headers,
    mode: 'cors',
    signal
  });
  const blob = await f.blob();
  callback(blob);
}
